import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that handles blog filtering based on metafield values.
 * 
 * @extends Component
 */
export default class BlogFilterComponent extends Component {
  /** @type {Set<string>} */
  #selectedValues = new Set();

  /** @type {Set<string>} */
  #selectedAuthorIds = new Set();

  /** @type {HTMLElement | null} */
  #buttonElement = null;

  /** @type {HTMLElement | null} */
  #dropdownElement = null;

  /** @type {HTMLElement | null} */
  #buttonTextElement = null;

  /** @type {NodeListOf<HTMLInputElement> | null} */
  #checkboxElements = null;

  /** @type {HTMLElement | null} */
  #tagsContainer = null;

  /** @type {HTMLElement | null} */
  #authorTagsContainer = null;

  /** @type {NodeListOf<HTMLElement> | null} */
  #authorCards = null;

  /** @type {HTMLElement | null} */
  #authorsContainer = null;

  /** @type {string} */
  #authorSelectedColorScheme = 'scheme-3';

  /** @type {HTMLElement | null} */
  #blogPostsContainer = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#buttonElement = this.querySelector('.blog-filter__button');
    this.#dropdownElement = this.querySelector('.blog-filter__dropdown');
    this.#buttonTextElement = this.querySelector('.blog-filter__button-text');
    this.#checkboxElements = this.querySelectorAll('.blog-filter__checkbox[data-filter-option]');
    this.#tagsContainer = this.querySelector('.blog-filter__tags');
    this.#authorTagsContainer = this.querySelector('.blog-filter__author-tags');
    this.#authorCards = this.querySelectorAll('.blog-filter__author-card');
    // Debug: log how many author cards we found
    // eslint-disable-next-line no-console
    console.log('[BlogFilter] Found author cards:', this.#authorCards?.length || 0);
    this.#authorsContainer = this.querySelector('.blog-filter__authors-list');
    if (this.#authorsContainer) {
      const scheme = this.#authorsContainer.dataset.authorSelectedColorScheme;
      if (scheme) {
        this.#authorSelectedColorScheme = scheme;
      }
    }
    
    // Find the blog posts container - it should be a sibling or parent element
    const blogPostsList = document.querySelector('blog-posts-list');
    this.#blogPostsContainer = blogPostsList?.querySelector('.blog-posts-container');

    if (!this.#buttonElement || !this.#dropdownElement) {
      console.warn('Blog filter elements not found');
      return;
    }

    // Initialize selected values from URL parameters
    this.#initializeFromURL();

    // Add event listeners
    this.#buttonElement.addEventListener('click', this.#handleButtonClick.bind(this));
    
    // Add change listeners to all checkboxes
    this.#checkboxElements.forEach(checkbox => {
      checkbox.addEventListener('change', this.#handleCheckboxChange.bind(this));
    });

    // Delegate click handling for author cards to the component (more robust)
    this.addEventListener('click', this.#handleAuthorClick.bind(this));

    // Close dropdown when clicking outside
    document.addEventListener('click', this.#handleOutsideClick.bind(this));

    // Handle keyboard navigation
    this.#dropdownElement.addEventListener('keydown', this.#handleKeyboardNavigation.bind(this));

    // Listen for new blog posts being added (pagination)
    if (this.#blogPostsContainer) {
      const observer = new MutationObserver(() => {
        // Re-apply filter when new posts are added
        this.#applyFilter();
      });
      
      observer.observe(this.#blogPostsContainer, {
        childList: true,
        subtree: false
      });
      
      // Store observer for cleanup
      this.#mutationObserver = observer;
    }

    // Apply initial filter after a short delay to ensure DOM is ready
    requestAnimationFrame(() => {
      this.#applyFilter();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#buttonElement) {
      this.#buttonElement.removeEventListener('click', this.#handleButtonClick.bind(this));
    }
    if (this.#checkboxElements) {
      this.#checkboxElements.forEach(checkbox => {
        checkbox.removeEventListener('change', this.#handleCheckboxChange.bind(this));
      });
    }
    if (this.#authorCards) {
      this.#authorCards.forEach(card => {
        card.removeEventListener('click', this.#handleAuthorClick.bind(this));
      });
    }
    document.removeEventListener('click', this.#handleOutsideClick.bind(this));
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
    }
  }

  /**
   * Initialize filter state from URL parameters
   */
  #initializeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');
    const authorParam = urlParams.get('author');
    
    if (filterParam) {
      const values = filterParam.split(',').map(v => decodeURIComponent(v.trim())).filter(Boolean);
      values.forEach(value => {
        const normalizedValue = value.trim();
        this.#selectedValues.add(normalizedValue);
        // Set the checkbox as checked - try both normalized and original value
        const checkbox = Array.from(this.#checkboxElements || []).find(
          cb => cb.value === normalizedValue || cb.value === value
        );
        if (checkbox) {
          checkbox.checked = true;
        }
      });
      // Uncheck "All" option if specific values are selected
      const allCheckbox = Array.from(this.#checkboxElements || []).find(cb => cb.value === '');
      if (allCheckbox) {
        allCheckbox.checked = false;
      }
    } else {
      // If no filter in URL, select "All" option (empty value)
      const allCheckbox = Array.from(this.#checkboxElements || []).find(cb => cb.value === '');
      if (allCheckbox) {
        allCheckbox.checked = true;
      }
      this.#selectedValues.clear();
    }

    // Initialize author filter from URL (multiple selection) - by author names (comma-separated) for readability
    if (authorParam) {
      const authorNamesFromURL = decodeURIComponent(authorParam).split(',').map(name => name.trim()).filter(Boolean);
      if (authorNamesFromURL.length > 0) {
        authorNamesFromURL.forEach(authorNameFromURL => {
          const normalizedFromURL = authorNameFromURL.toLowerCase();
          const authorCard = Array.from(this.#authorCards || []).find(
            card => (card.dataset.authorName || '').trim().toLowerCase() === normalizedFromURL
          );
          if (authorCard) {
            // Use author name as the selection key
            this.#selectedAuthorIds.add(normalizedFromURL);
            authorCard.setAttribute('aria-pressed', 'true');
            // apply selected color scheme
            authorCard.classList.add(`color-${this.#authorSelectedColorScheme}`);
          }
        });
      }
    }

    this.#updateButtonText();
    this.#updateTags();
    this.#updateAuthorTags();
  }

  /**
   * Handle button click to toggle dropdown
   */
  #handleButtonClick(event) {
    event.stopPropagation();
    const isExpanded = this.#buttonElement?.getAttribute('aria-expanded') === 'true';
    this.#toggleDropdown(!isExpanded);
  }

  /**
   * Handle checkbox change event
   */
  #handleCheckboxChange(event) {
    const checkbox = event.target;
    const value = checkbox.value;

    if (value === '') {
      // "All" option clicked
      if (checkbox.checked) {
        // Select "All" - uncheck all other options
        this.#selectedValues.clear();
        this.#checkboxElements?.forEach(cb => {
          if (cb.value !== '') {
            cb.checked = false;
          }
        });
      }
    } else {
      // Specific option clicked
      const normalizedValue = value.trim();
      if (checkbox.checked) {
        this.#selectedValues.add(normalizedValue);
        // Uncheck "All" option if specific option is selected
        const allCheckbox = Array.from(this.#checkboxElements || []).find(cb => cb.value === '');
        if (allCheckbox) {
          allCheckbox.checked = false;
        }
      } else {
        this.#selectedValues.delete(normalizedValue);
        // Also try to delete the original value in case it wasn't normalized
        this.#selectedValues.delete(value);
        // If no specific options selected, check "All"
        if (this.#selectedValues.size === 0) {
          const allCheckbox = Array.from(this.#checkboxElements || []).find(cb => cb.value === '');
          if (allCheckbox) {
            allCheckbox.checked = true;
          }
        }
      }
    }

    // Update button text
    this.#updateButtonText();

    // Update tags display
    this.#updateTags();
    this.#updateAuthorTags();

    // Update URL without page reload
    this.#updateURL();
    
    // Apply the filter
    this.#applyFilter();
    
    // Dispatch filter update event for pagination
    document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
  }

  /**
   * Handle clicks outside the dropdown to close it
   */
  #handleOutsideClick(event) {
    if (this.#dropdownElement && !this.contains(event.target)) {
      this.#toggleDropdown(false);
    }
  }

  /**
   * Handle keyboard navigation in dropdown
   */
  #handleKeyboardNavigation(event) {
    if (event.key === 'Escape') {
      this.#toggleDropdown(false);
      this.#buttonElement?.focus();
    }
  }

  /**
   * Toggle dropdown open/closed state
   */
  #toggleDropdown(isOpen) {
    if (!this.#buttonElement || !this.#dropdownElement) return;

    this.#buttonElement.setAttribute('aria-expanded', isOpen.toString());
    if (isOpen) {
      this.#dropdownElement.removeAttribute('hidden');
    } else {
      this.#dropdownElement.setAttribute('hidden', '');
    }
  }

  /**
   * Update button text based on selected values
   */
  #updateButtonText() {
    if (!this.#buttonTextElement) return;

    // Get custom selected label from data attribute
    const selectedLabel = this.dataset.selectedLabel || 'selected';

    if (this.#selectedValues.size === 0) {
      // Find "All" option label
      const allCheckbox = Array.from(this.#checkboxElements || []).find(cb => cb.value === '');
      if (allCheckbox) {
        const label = allCheckbox.closest('.blog-filter__option')?.querySelector('.blog-filter__option-label');
        this.#buttonTextElement.textContent = label?.textContent || 'All Types';
      }
    } else if (this.#selectedValues.size === 1) {
      // Show single selected value
      const value = Array.from(this.#selectedValues)[0];
      const checkbox = Array.from(this.#checkboxElements || []).find(cb => cb.value === value);
      if (checkbox) {
        const label = checkbox.closest('.blog-filter__option')?.querySelector('.blog-filter__option-label');
        this.#buttonTextElement.textContent = label?.textContent || value;
      }
    } else {
      // Show count of selected items with custom label
      this.#buttonTextElement.textContent = `${this.#selectedValues.size} ${selectedLabel}`;
    }
  }

  /**
   * Handle author card click (multiple selection)
   */
  #handleAuthorClick(event) {
    // Find the author card that was clicked (event delegation)
    const card = event.target.closest('.blog-filter__author-card');
    if (!card || !this.contains(card)) {
      return;
    }

    // Use author name as the selection key
    const authorName = (card.dataset?.authorName || '').trim();
    if (!authorName) {
      return;
    }
    const authorKey = authorName.toLowerCase();

    // eslint-disable-next-line no-console
    console.log('[BlogFilter] Author card clicked', { authorKey, name: authorName });

    const isPressed = card.getAttribute('aria-pressed') === 'true';

    if (isPressed) {
      // Deselect author
      this.#selectedAuthorIds.delete(authorKey);
      card.setAttribute('aria-pressed', 'false');
      card.classList.remove(`color-${this.#authorSelectedColorScheme}`);
    } else {
      // Select author (allow multiple selections)
      this.#selectedAuthorIds.add(authorKey);
      card.setAttribute('aria-pressed', 'true');
      card.classList.add(`color-${this.#authorSelectedColorScheme}`);
    }

    // Update URL without page reload
    this.#updateURL();
    
    // Apply the filter
    this.#applyFilter();
    
    // Update tags display
    this.#updateTags();
    this.#updateAuthorTags();
    
    // Dispatch filter update event for pagination
    document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
  }

  /**
   * Update URL with current filter state
   */
  #updateURL() {
    const url = new URL(window.location.href);
    
    if (this.#selectedValues.size === 0) {
      // No filter selected, remove filter parameter
      url.searchParams.delete('filter');
    } else {
      // Add filter parameter with selected values
      const filterValue = Array.from(this.#selectedValues).map(v => encodeURIComponent(v)).join(',');
      url.searchParams.set('filter', filterValue);
    }

    // Get all selected authors by pressed state (source of truth)
    const pressedAuthorCards = Array.from(this.#authorCards || []).filter(
      card => card.getAttribute('aria-pressed') === 'true'
    );
    const selectedAuthorNames = pressedAuthorCards
      .map(card => (card.dataset.authorName || '').trim())
      .filter(Boolean);
    
    if (selectedAuthorNames.length === 0) {
      // No authors selected, remove author parameter
      url.searchParams.delete('author');
    } else {
      // Add author parameter with selected author names (comma-separated, human-readable)
      const authorValue = selectedAuthorNames.map(name => encodeURIComponent(name)).join(',');
      url.searchParams.set('author', authorValue);
    }
    
    // Remove page parameter when filtering changes
    url.searchParams.delete('page');
    
    // Update URL without reload
    window.history.pushState({}, '', url.toString());
  }

  /**
   * Apply filter to blog posts
   */
  #applyFilter() {
    if (!this.#blogPostsContainer) {
      // Try to find it again
      const blogPostsList = document.querySelector('blog-posts-list');
      this.#blogPostsContainer = blogPostsList?.querySelector('.blog-posts-container');
      
      if (!this.#blogPostsContainer) {
        // Wait a bit for DOM to be ready
        requestAnimationFrame(() => {
          this.#applyFilter();
        });
        return;
      }
    }

    const blogPostItems = this.#blogPostsContainer.querySelectorAll('.blog-post-item');
    
    // Check if any filters are active
    const hasTypeFilter = this.#selectedValues.size > 0;
    let hasAuthorFilter = this.#selectedAuthorIds.size > 0;

    // Get all selected author names once before the loop (for efficiency)
    let selectedAuthorNames = [];
    if (hasAuthorFilter) {
      // Re-query author cards if not found (in case of timing issues)
      if (!this.#authorCards || this.#authorCards.length === 0) {
        this.#authorCards = this.querySelectorAll('.blog-filter__author-card');
      }
      
      const selectedAuthorCards = Array.from(this.#authorCards || []).filter(
        card => card.getAttribute('aria-pressed') === 'true'
      );
      
      selectedAuthorNames = selectedAuthorCards
        .map(card => (card.dataset.authorName || '').trim())
        .filter(Boolean);

      // If no author cards are actually selected, treat as no author filter
      if (selectedAuthorNames.length === 0) {
        hasAuthorFilter = false;
      }
    }

    const hasAnyFilter = hasTypeFilter || hasAuthorFilter;

    if (!hasAnyFilter) {
      // No filters selected, show all posts
      blogPostItems.forEach(item => {
        const htmlItem = item instanceof HTMLElement ? item : null;
        if (htmlItem) {
          htmlItem.style.display = '';
          htmlItem.removeAttribute('hidden');
        }
      });
    } else {
      // Filter posts based on selected values (AND logic - must match both type and author if both are selected)
      blogPostItems.forEach(item => {
        const htmlItem = item instanceof HTMLElement ? item : null;
        if (!htmlItem) return;
        
        // Initialize match flags
        // Default to true so that if a filter is not active, it doesn't exclude the post
        let matchesType = !hasTypeFilter; // true if no type filter, false if type filter is active (will be set below)
        let matchesAuthor = !hasAuthorFilter; // true if no author filter, false if author filter is active (will be set below)

        // Check type filter (OR logic: post matches if ANY selected type matches ANY of the post's types)
        if (hasTypeFilter) {
          matchesType = false; // Start as false, will be set to true if match found
          const metafieldValueRaw = (htmlItem.dataset.blogType || '').trim();
          
          if (metafieldValueRaw && metafieldValueRaw !== '') {
            // Parse the metafield value - it might be a JSON array string or a plain string
            let metafieldValues = [];
            try {
              // Try to parse as JSON array first
              const parsed = JSON.parse(metafieldValueRaw);
              if (Array.isArray(parsed)) {
                metafieldValues = parsed.map(v => String(v).trim()).filter(Boolean);
              } else {
                // If it's not an array, treat it as a single value
                metafieldValues = [String(parsed).trim()].filter(Boolean);
              }
            } catch (e) {
              // If parsing fails, treat it as a plain string
              metafieldValues = [metafieldValueRaw.trim()].filter(Boolean);
            }

            // OR logic: Check if ANY of the selected types matches ANY of the post's types
            // Normalize both values for comparison (trim and case-insensitive)
            matchesType = Array.from(this.#selectedValues).some(selectedValue => {
              const normalizedSelected = (selectedValue || '').trim().toLowerCase();
              
              return metafieldValues.some(metafieldValue => {
                const normalizedMetafield = metafieldValue.trim().toLowerCase();
                return normalizedMetafield === normalizedSelected;
              });
            });
          }
          // If metafieldValueRaw is empty, matchesType stays false (post doesn't match)
        }

        // Check author filter (OR logic: post matches if it has ANY of the selected authors)
        if (hasAuthorFilter && selectedAuthorNames.length > 0) {
          matchesAuthor = false; // Start as false, will be set to true if match found
          // Find the blog-post-card inside the blog-post-item
          const blogPostCard = htmlItem.querySelector('.blog-post-card');
          
          if (blogPostCard && blogPostCard instanceof HTMLElement) {
            // Get author names from data-author attribute (comma-separated)
            const authorNamesRaw = (blogPostCard.dataset.author || '').trim();
            
            if (authorNamesRaw && authorNamesRaw !== '') {
              // Parse post author names (comma-separated) and normalize for comparison
              const postAuthorNames = authorNamesRaw.split(',').map((name) => name.trim()).filter(Boolean);
              
              // Check if ANY of the post's authors match ANY of the selected authors (OR logic)
              matchesAuthor = postAuthorNames.some((postAuthorName) => {
                const normalizedPostName = postAuthorName.trim().toLowerCase();
                return selectedAuthorNames.some((selectedAuthorName) => {
                  const normalizedSelectedName = selectedAuthorName.trim().toLowerCase();
                  return normalizedPostName === normalizedSelectedName;
                });
              });
            }
            // If authorNamesRaw is empty, matchesAuthor stays false
          }
          // If blogPostCard not found, matchesAuthor stays false
        }

        // AND logic: Show post only if it matches ALL active filters
        // - If only type filter is active: must match type (matchesAuthor is true by default)
        // - If only author filter is active: must match author (matchesType is true by default)
        // - If both filters are active: must match BOTH type AND author
        if (matchesType && matchesAuthor) {
          htmlItem.style.display = '';
          htmlItem.removeAttribute('hidden');
        } else {
          htmlItem.style.display = 'none';
          htmlItem.setAttribute('hidden', '');
        }
      });
    }

    // Update visible count
    const visibleCount = Array.from(blogPostItems).filter(item => {
      const htmlItem = item instanceof HTMLElement ? item : null;
      if (!htmlItem) return false;
      return !htmlItem.hasAttribute('hidden') && htmlItem.style.display !== 'none';
    }).length;
    
    // Dispatch custom event with filter results
    this.dispatchEvent(new CustomEvent('blog-filter-applied', {
      detail: {
        selectedValues: Array.from(this.#selectedValues),
        visibleCount,
        totalCount: blogPostItems.length
      },
      bubbles: true
    }));
  }

  /**
   * Update the filter tags display
   */
  #updateTags() {
    if (!this.#tagsContainer) return;

    // Clear existing tags
    this.#tagsContainer.innerHTML = '';

    // Don't show tags if no type filters are selected (author filters don't show as tags)
    if (this.#selectedValues.size === 0) {
      return;
    }

    // Create a tag for each selected type value
    Array.from(this.#selectedValues).forEach(value => {
      // Find the label text for this value
      const checkbox = Array.from(this.#checkboxElements || []).find(cb => cb.value === value);
      const label = checkbox?.closest('.blog-filter__option')?.querySelector('.blog-filter__option-label');
      const displayText = label?.textContent || value;
      
      // Create tag element
      const tag = document.createElement('div');
      tag.className = 'blog-filter__tag';
      tag.setAttribute('role', 'listitem');
      tag.setAttribute('data-filter-value', value);
      tag.setAttribute('data-filter-type', 'type');

      // Tag text
      const tagText = document.createElement('span');
      tagText.className = 'blog-filter__tag-text';
      tagText.textContent = displayText;

      // Remove button
      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'blog-filter__tag-remove';
      removeButton.setAttribute('aria-label', `Remove ${displayText} filter`);
      removeButton.innerHTML = `
        <svg class="blog-filter__tag-remove-icon" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      removeButton.addEventListener('click', () => {
        // Uncheck the corresponding checkbox
        if (checkbox) {
          checkbox.checked = false;
          // Trigger change event to update state
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      tag.appendChild(tagText);
      tag.appendChild(removeButton);
      this.#tagsContainer.appendChild(tag);
    });
  }

  /**
   * Update author tags display
   */
  #updateAuthorTags() {
    // Re-query container if not found (in case of timing issues)
    if (!this.#authorTagsContainer) {
      this.#authorTagsContainer = this.querySelector('.blog-filter__author-tags');
    }
    
    if (!this.#authorTagsContainer) return;

    // Clear existing tags
    this.#authorTagsContainer.innerHTML = '';

    // Re-query author cards if not found (in case of timing issues)
    if (!this.#authorCards || this.#authorCards.length === 0) {
      this.#authorCards = this.querySelectorAll('.blog-filter__author-card');
    }

    // Get all selected author cards (use aria-pressed as source of truth)
    const selectedAuthorCards = Array.from(this.#authorCards || []).filter(
      card => card.getAttribute('aria-pressed') === 'true'
    );

    // Don't show tags if no authors are selected
    if (selectedAuthorCards.length === 0) {
      return;
    }

    // Create a tag for each selected author
    selectedAuthorCards.forEach(selectedAuthorCard => {
      const authorName = (selectedAuthorCard.dataset.authorName || '').trim();
      if (!authorName) {
        return;
      }

      const authorKey = authorName.toLowerCase();

      // Create tag element
      const tag = document.createElement('div');
      tag.className = 'blog-filter__tag';
      tag.setAttribute('role', 'listitem');
      tag.setAttribute('data-filter-type', 'author');
      tag.setAttribute('data-author-name', authorName);

      // Tag text
      const tagText = document.createElement('span');
      tagText.className = 'blog-filter__tag-text';
      tagText.textContent = authorName;

      // Remove button
      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'blog-filter__tag-remove';
      removeButton.setAttribute('aria-label', `Remove ${authorName} filter`);
      removeButton.innerHTML = `
        <svg class="blog-filter__tag-remove-icon" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      removeButton.addEventListener('click', (event) => {
        // Prevent bubbling so card click handler doesn't also fire
        event.stopPropagation();

        const nameFromTag = (tag.getAttribute('data-author-name') || '').trim();
        if (!nameFromTag) {
          return;
        }
        const keyFromTag = nameFromTag.toLowerCase();

        // Re-query author cards and find matching by name
        if (!this.#authorCards || this.#authorCards.length === 0) {
          this.#authorCards = this.querySelectorAll('.blog-filter__author-card');
        }

        const authorCardToDeselect = Array.from(this.#authorCards || []).find(
          card => (card.dataset.authorName || '').trim().toLowerCase() === keyFromTag
        );

        if (authorCardToDeselect) {
          // Deselect the author card
          authorCardToDeselect.setAttribute('aria-pressed', 'false');
          authorCardToDeselect.classList.remove(`color-${this.#authorSelectedColorScheme}`);
        }

        // Remove from our selection set
        this.#selectedAuthorIds.delete(keyFromTag);

        // Update tags
        this.#updateAuthorTags();

        // Update URL
        this.#updateURL();

        // Apply filter
        this.#applyFilter();

        // Dispatch filter update event
        document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
      });

      tag.appendChild(tagText);
      tag.appendChild(removeButton);
      this.#authorTagsContainer.appendChild(tag);
    });
  }
}

if (!customElements.get('blog-filter-component')) {
  customElements.define('blog-filter-component', BlogFilterComponent);
}

